import { API_IDS } from '@intigral/constant';
import { DI } from '@intigral/constant/config/DIContainer';
import { IThinkAnalyticsRepository } from '../../core/repositories/interfaces/IThinkAnalyticsRepository';
import { container } from '../../DataDomain';
import { IHttpAdapter } from '../../entity/interface/iHttpAdapter';
import { IApiPathManager } from '../config/ApiPathManager';
import {
    IThinkAnalyticsGetMoreLikeThisResponse,
    IThinkAnalyticsGetFeelingLucky,
    IThinkAnalyticsgetMoodVod,
    IThinkAnalyticsGetZone,
} from '../../entity/interface/iThinkAnalytics';

class ThinkAnalyticsRepository implements IThinkAnalyticsRepository {
    // Lazy getters for dependencies to avoid circular dependencies
    private get http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiPathManager(): IApiPathManager {
        return container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    async getVodListAndYouWatched(
        apiKey: string,
        token: string,
        queryParams: any
    ): Promise<IThinkAnalyticsGetMoreLikeThisResponse> {
        const response = await this.http.get<IThinkAnalyticsGetMoreLikeThisResponse>(
            this.apiPathManager.getPath(API_IDS.THINK_ANALYTICS_API_GET_VODLIST_AND_YOU_WATCHED),
            {
                params: {
                    apikey: apiKey,
                    ...queryParams,
                },
                headers: {
                    identityToken: token,
                },
            }
        );
        return response.data;
    }

    async getRelated(apiKey: string, token: string, queryParams: any): Promise<IThinkAnalyticsGetMoreLikeThisResponse> {
        const response = await this.http.get<IThinkAnalyticsGetMoreLikeThisResponse>(
            this.apiPathManager.getPath(API_IDS.THINK_ANALYTICS_API_GET_RELATED),
            {
                params: {
                    apiKey,
                    ...queryParams,
                },
                headers: {
                    identityToken: token,
                },
            }
        );
        return response.data;
    }

    async getRelatedOthers(
        apiKey: string,
        token: string,
        queryParams: any
    ): Promise<IThinkAnalyticsGetMoreLikeThisResponse> {
        const response = await this.http.get<IThinkAnalyticsGetMoreLikeThisResponse>(
            this.apiPathManager.getPath(API_IDS.THINK_ANALYTICS_API_GET_RELATED_OTHERS),
            {
                params: {
                    apiKey,
                    ...queryParams,
                },
                headers: {
                    identityToken: token,
                },
            }
        );
        return response.data;
    }

    async getBecauseYouWatchedMultiple(
        apiKey: string,
        token: string,
        queryParams: any
    ): Promise<IThinkAnalyticsGetMoreLikeThisResponse> {
        const response = await this.http.get<IThinkAnalyticsGetMoreLikeThisResponse>(
            this.apiPathManager.getPath(API_IDS.THINK_ANALYTICS_API_GET_BECAUSE_YOU_WATCH_MULTIPLE),
            {
                params: {
                    apikey: apiKey,
                    ...queryParams,
                },
                headers: {
                    identityToken: token,
                },
            }
        );
        return response.data;
    }

    async getTAByUsecaseId(
        datasource: string,
        apiKey: string,
        token: string,
        queryParams: any
    ): Promise<IThinkAnalyticsGetMoreLikeThisResponse> {
        const path = this.apiPathManager.getByDatasource(datasource);
        // console.log('Generic TA Feed for datasource path', path);
        const response = await this.http.get<IThinkAnalyticsGetMoreLikeThisResponse>(path, {
            params: {
                apiKey,
                apikey: apiKey,
                ...queryParams,
            },
            headers: {
                identityToken: token,
            },
        });
        return response.data;
    }

    async getFeelingLucky(
        apikey: string,
        token: string,
        profileName: string,
        userName: string,
        device: string,
        country: string,
        bySubscriptionGuidsPipeEncoded: string,
        rating: string,
        isKidsMode: boolean
    ): Promise<IThinkAnalyticsGetFeelingLucky> {
        const response = await this.http.get<IThinkAnalyticsGetFeelingLucky>(
            this.apiPathManager.getPath(API_IDS.THINK_ANALYTICS_API_GET_FEELING_LUCKY),
            {
                params: {
                    apikey,
                    profileName,
                    userName,
                    device,
                    country,
                    bySubscriptionGuidsPipeEncoded,
                    rating,
                    ...(isKidsMode && { categoryType: 'kids' }),
                },
                headers: {
                    identityToken: token,
                },
            }
        );
        return response.data;
    }

    async getMoodVod(
        apiKey: string,
        token: string,
        profileName: string,
        userName: string,
        device: string,
        country: string,
        subGuids: string,
        rating: string,
        useCaseId: string
    ): Promise<IThinkAnalyticsgetMoodVod> {
        const response = await this.http.get<IThinkAnalyticsgetMoodVod>(
            this.apiPathManager.getPath(API_IDS.THINK_ANALYTICS_MOOD_VOD_API),
            {
                params: {
                    apiKey,
                    profileName,
                    userName,
                    device,
                    country,
                    subGuids,
                    rating,
                    useCaseId,
                },
                headers: {
                    identityToken: token,
                },
            }
        );
        return response.data;
    }

    async getZone(
        url: string,
        token: string,
        apiKey: string,
        profileName: string,
        userName: string
    ): Promise<IThinkAnalyticsGetZone> {
        const response = await this.http.get<IThinkAnalyticsGetZone>(url, {
            params: {
                apiKey,
                profileName,
                userName,
            },
            headers: {
                identityToken: token,
            },
        });

        return response.data;
    }
}

export default ThinkAnalyticsRepository;
